import { enqueueSnackbar } from "notistack";

/**
 * Extract the server error message from an Apollo mutation error
 * (or a plain `Error` thrown by helpers such as `uploadFile`).
 */
const getMutationErrorMessage = (error: unknown): string | undefined => {
  const err = error as
    | {
        graphQLErrors?: ReadonlyArray<{ message?: string }>;
        errors?: ReadonlyArray<{ message?: string }>;
        message?: string;
      }
    | undefined;

  return err?.graphQLErrors?.[0]?.message ?? err?.errors?.[0]?.message ?? err?.message;
};

export interface MutationErrorOptions {
  /** Called after a `no rows in result set` denial — re-fetch the row/list. The mutation is never retried. */
  onRowGone?: () => void;
}

/**
 * Centralised mutation error toast for the circle permission layer
 * (docs/plans/circle-feature-plan.md, mutation and upload errors).
 *
 * - `access denied*` → toast the raw server message verbatim.
 * - `no rows in result set` → friendly toast + `options.onRowGone` (re-fetch the list/row);
 *   never retry the mutation.
 * - anything else → toast the raw message. Never swallow.
 *
 * Wire to Apollo with `onError: (error) => handleMutationError(error)` —
 * the function is not directly assignable to Apollo's `onError` signature
 * because of the optional second argument.
 */
export const handleMutationError = (error: unknown, options?: MutationErrorOptions): void => {
  const message = getMutationErrorMessage(error);
  if (!message) {
    return;
  }
  if (message === "no rows in result set") {
    enqueueSnackbar("This task is not available in your current context.", { variant: "error" });
    options?.onRowGone?.();
    return;
  }
  enqueueSnackbar(message, { variant: "error" });
};
